/**
 * When she last used the app: a tap or a key, in any tab of this browser.
 *
 * - The 15-minute idle lock (PRD 12) reads it, so a tab left open on a statement does not sign
 *   out the tab she is working in: tabs share one timestamp in localStorage.
 * - Requests sent after a minute without a tap or a key are background (X-Background: 1): polls
 *   then never keep the server's session open on an unattended phone or console.
 */
const KEY = 'tisunge.activity'
/** Write the shared timestamp at most this often; the lock works in minutes. */
const WRITE_EVERY_MS = 15_000
/** A request this long after her last tap or key is a poll, not something she did. */
export const BACKGROUND_AFTER_MS = 60_000

let lastHere = Date.now()
let lastWritten = 0

function readShared(): number {
  try {
    return Number(window.localStorage.getItem(KEY)) || 0
  } catch {
    return 0
  }
}

/** She did something here, now. */
export function markActive(now = Date.now()): void {
  lastHere = now
  if (now - lastWritten < WRITE_EVERY_MS) return
  lastWritten = now
  try {
    window.localStorage.setItem(KEY, String(now))
  } catch {
    // storage unavailable: this tab still counts its own activity
  }
}

/** The latest tap or key recorded by any tab before this page loaded (a cold start reads this one). */
export function sharedLastActive(): number {
  return readShared()
}

/** The latest tap or key in any tab. */
export function lastActive(): number {
  return Math.max(lastHere, readShared())
}

export function idleFor(now = Date.now()): number {
  return now - lastActive()
}

/** Listen for taps and keys; returns the clean-up. */
export function trackActivity(): () => void {
  const touch = () => markActive()
  const events = ['pointerdown', 'keydown'] as const
  events.forEach((name) => window.addEventListener(name, touch, { passive: true }))
  return () => events.forEach((name) => window.removeEventListener(name, touch))
}

/** For tests: start counting from now. */
export function resetActivity(now = Date.now()): void {
  lastHere = now
  lastWritten = 0
}
